import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { 
  ArrowLeft, RefreshCw, Search, DollarSign, ArrowUpRight, ArrowDownLeft,
  Building2, Ticket, Users, TrendingUp, Calendar, Filter, Download,
  ChevronLeft, ChevronRight, Wallet, CreditCard, Award
} from 'lucide-react';
import api from '../../utils/api';
import { toast } from 'sonner';

const TransactionLedger = () => {
  const [loading, setLoading] = useState(true);
  const [summary, setSummary] = useState(null);
  const [transactions, setTransactions] = useState([]);
  const [dailyVolume, setDailyVolume] = useState([]);
  const [filter, setFilter] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [pagination, setPagination] = useState({ offset: 0, limit: 100, total: 0 });

  useEffect(() => {
    fetchData();
  }, [filter, pagination.offset]);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [summaryRes, transactionsRes, volumeRes] = await Promise.all([
        api.get('/admin/ledger/summary'),
        api.get(`/admin/ledger/transactions?type=${filter}&limit=${pagination.limit}&offset=${pagination.offset}&search=${searchTerm}`),
        api.get('/admin/ledger/daily-volume?days=7')
      ]);
      
      setSummary(summaryRes.data);
      setTransactions(transactionsRes.data.transactions || []);
      setPagination(prev => ({ ...prev, total: transactionsRes.data.total || 0 }));
      setDailyVolume(volumeRes.data || []);
    } catch (error) {
      console.error('Failed to fetch ledger data:', error);
      toast.error('Failed to load transaction ledger');
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = () => {
    setPagination(prev => ({ ...prev, offset: 0 }));
    fetchData();
  };

  const exportCSV = () => {
    const headers = ['Date', 'Type', 'From', 'To', 'Amount', 'Description', 'Status'];
    const rows = transactions.map(t => [
      t.created_at ? new Date(t.created_at).toLocaleString() : 'N/A',
      t.type_label,
      t.from_user?.name || 'System',
      t.to_user?.name || 'System',
      t.amount,
      t.description,
      t.status
    ]);
    
    const csv = [headers, ...rows].map(row => row.join(',')).join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `transaction_ledger_${new Date().toISOString().split('T')[0]}.csv`;
    a.click();
    toast.success('CSV exported!');
  };

  const getTypeIcon = (type) => {
    switch (type) {
      case 'transfer': return <ArrowUpRight className="w-4 h-4" />;
      case 'pass': return <Ticket className="w-4 h-4" />;
      case 'franchise': return <Building2 className="w-4 h-4" />;
      case 'commission': return <Award className="w-4 h-4" />;
      case 'payment': return <CreditCard className="w-4 h-4" />;
      default: return <DollarSign className="w-4 h-4" />;
    }
  };

  const getTypeColor = (type) => {
    switch (type) {
      case 'transfer': return 'text-blue-500 bg-blue-500/20';
      case 'pass': return 'text-purple-500 bg-purple-500/20';
      case 'franchise': return 'text-amber-500 bg-amber-500/20';
      case 'commission': return 'text-green-500 bg-green-500/20';
      case 'payment': return 'text-cyan-500 bg-cyan-500/20';
      default: return 'text-gray-500 bg-gray-500/20';
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'completed': return 'bg-green-500/20 text-green-500';
      case 'active': return 'bg-green-500/20 text-green-500';
      case 'pending': return 'bg-yellow-500/20 text-yellow-500';
      case 'failed': return 'bg-red-500/20 text-red-500';
      case 'suspended': return 'bg-red-500/20 text-red-500';
      default: return 'bg-gray-500/20 text-gray-500';
    }
  };

  const maxVolume = Math.max(...dailyVolume.map(d => d.volume), 1);

  return (
    <div className="min-h-screen pt-24 pb-16 px-6" data-testid="transaction-ledger">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div>
            <Link to="/admin" className="text-muted-foreground hover:text-white text-sm flex items-center gap-1 mb-2">
              <ArrowLeft className="w-4 h-4" /> Back to Admin
            </Link>
            <h1 className="text-3xl font-bold flex items-center gap-3">
              <Wallet className="w-8 h-8 text-emerald-500" />
              Transaction Ledger
            </h1>
            <p className="text-muted-foreground mt-1">Complete financial overview</p>
          </div>
          <div className="flex gap-2">
            <button 
              onClick={exportCSV}
              className="p-3 rounded-xl bg-emerald-500/20 text-emerald-500 hover:bg-emerald-500/30"
              title="Export CSV"
            >
              <Download className="w-5 h-5" />
            </button>
            <button 
              onClick={fetchData} 
              className="p-3 rounded-xl bg-white/10 hover:bg-white/20"
            >
              <RefreshCw className={`w-5 h-5 ${loading ? 'animate-spin' : ''}`} />
            </button>
          </div>
        </div>

        {/* Summary Stats */}
        {summary && (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
            <div className="glass-card p-5">
              <div className="flex items-center gap-3 mb-2">
                <ArrowUpRight className="w-7 h-7 text-blue-500" />
                <span className="text-sm text-muted-foreground">Wallet Transfers</span>
              </div>
              <p className="text-2xl font-bold">₹{(summary.wallet_transfers?.total || 0).toLocaleString()}</p>
              <p className="text-sm text-muted-foreground">{summary.wallet_transfers?.count || 0} transactions</p>
            </div>
            <div className="glass-card p-5">
              <div className="flex items-center gap-3 mb-2">
                <Ticket className="w-7 h-7 text-purple-500" />
                <span className="text-sm text-muted-foreground">Pass Sales</span>
              </div>
              <p className="text-2xl font-bold">₹{(summary.pass_purchases?.total || 0).toLocaleString()}</p>
              <p className="text-sm text-muted-foreground">{summary.pass_purchases?.count || 0} purchases</p>
            </div>
            <div className="glass-card p-5">
              <div className="flex items-center gap-3 mb-2">
                <Building2 className="w-7 h-7 text-amber-500" />
                <span className="text-sm text-muted-foreground">Franchise Revenue</span>
              </div>
              <p className="text-2xl font-bold">₹{(summary.franchise_purchases?.total || 0).toLocaleString()}</p>
              <p className="text-sm text-muted-foreground">{summary.franchise_purchases?.count || 0} franchises</p>
            </div>
            <div className="glass-card p-5">
              <div className="flex items-center gap-3 mb-2">
                <Award className="w-7 h-7 text-green-500" />
                <span className="text-sm text-muted-foreground">Commissions Paid</span>
              </div>
              <p className="text-2xl font-bold">₹{(summary.commissions?.total || 0).toLocaleString()}</p>
              <p className="text-sm text-muted-foreground">{summary.commissions?.count || 0} payouts</p>
            </div>
          </div>
        )}

        {/* Activity Stats */}
        {summary && (
          <div className="grid md:grid-cols-2 gap-6 mb-8">
            {/* Daily Volume Chart */}
            <div className="glass-card p-6">
              <h3 className="font-bold mb-4 flex items-center gap-2">
                <TrendingUp className="w-5 h-5 text-emerald-500" />
                7-Day Volume
              </h3>
              <div className="flex items-end gap-2 h-32">
                {dailyVolume.map((day, i) => (
                  <div key={i} className="flex-1 flex flex-col items-center gap-1">
                    <div 
                      className="w-full bg-emerald-500/30 rounded-t transition-all hover:bg-emerald-500/50"
                      style={{ height: `${(day.volume / maxVolume) * 100}%`, minHeight: '4px' }}
                      title={`₹${day.volume.toLocaleString()}`}
                    />
                    <span className="text-xs text-muted-foreground">{day.day}</span>
                  </div>
                ))}
              </div>
            </div>

            {/* Activity Summary */}
            <div className="glass-card p-6">
              <h3 className="font-bold mb-4 flex items-center gap-2">
                <Calendar className="w-5 h-5 text-blue-500" />
                Activity Summary
              </h3>
              <div className="space-y-4">
                <div className="flex items-center justify-between p-3 bg-white/5 rounded-lg">
                  <span className="text-muted-foreground">Today's Transactions</span>
                  <span className="font-bold text-xl">{summary.activity?.today || 0}</span>
                </div>
                <div className="flex items-center justify-between p-3 bg-white/5 rounded-lg">
                  <span className="text-muted-foreground">This Week</span>
                  <span className="font-bold text-xl">{summary.activity?.this_week || 0}</span>
                </div>
                <div className="flex items-center justify-between p-3 bg-emerald-500/10 rounded-lg border border-emerald-500/30">
                  <span className="text-emerald-500">Total Volume</span>
                  <span className="font-bold text-xl text-emerald-500">
                    ₹{(
                      (summary.wallet_transfers?.total || 0) +
                      (summary.pass_purchases?.total || 0) +
                      (summary.franchise_purchases?.total || 0)
                    ).toLocaleString()}
                  </span>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Filters */}
        <div className="flex flex-wrap gap-4 mb-6">
          <div className="flex gap-2">
            {[
              { value: '', label: 'All' },
              { value: 'transfer', label: 'Transfers' },
              { value: 'pass', label: 'Passes' },
              { value: 'franchise', label: 'Franchises' },
              { value: 'commission', label: 'Commissions' },
              { value: 'payment', label: 'Payments' }
            ].map((f) => (
              <button
                key={f.value}
                onClick={() => { setFilter(f.value); setPagination(prev => ({ ...prev, offset: 0 })); }}
                className={`px-4 py-2 rounded-lg font-medium transition-all ${
                  filter === f.value ? 'bg-emerald-500 text-white' : 'bg-white/10 hover:bg-white/20'
                }`}
              >
                {f.label}
              </button>
            ))}
          </div>
          <div className="relative flex-1 max-w-xs flex gap-2">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
              <input
                type="text"
                placeholder="Search transactions..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
                className="w-full pl-10 pr-4 py-2 bg-white/10 border border-white/10 rounded-lg"
              />
            </div>
            <button onClick={handleSearch} className="px-4 py-2 bg-white/10 rounded-lg hover:bg-white/20">
              <Filter className="w-4 h-4" />
            </button>
          </div>
        </div>

        {/* Transactions Table */}
        <div className="glass-card overflow-hidden">
          <div className="p-4 border-b border-white/10">
            <h3 className="font-bold">All Transactions ({pagination.total})</h3>
          </div>
          
          {loading ? (
            <div className="p-8 text-center">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-emerald-500 mx-auto"></div>
            </div>
          ) : transactions.length > 0 ? (
            <>
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-white/5">
                    <tr>
                      <th className="px-4 py-3 text-left text-sm font-medium text-muted-foreground">Type</th>
                      <th className="px-4 py-3 text-left text-sm font-medium text-muted-foreground">From</th>
                      <th className="px-4 py-3 text-left text-sm font-medium text-muted-foreground">To</th>
                      <th className="px-4 py-3 text-right text-sm font-medium text-muted-foreground">Amount</th>
                      <th className="px-4 py-3 text-left text-sm font-medium text-muted-foreground">Description</th>
                      <th className="px-4 py-3 text-center text-sm font-medium text-muted-foreground">Status</th>
                      <th className="px-4 py-3 text-right text-sm font-medium text-muted-foreground">Date</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-white/5">
                    {transactions.map((t, i) => (
                      <tr key={t.id || i} className="hover:bg-white/5 transition-colors">
                        <td className="px-4 py-3">
                          <div className="flex items-center gap-2">
                            <span className={`p-2 rounded-lg ${getTypeColor(t.type)}`}>
                              {getTypeIcon(t.type)}
                            </span>
                            <span className="text-sm font-medium">{t.type_label}</span>
                          </div>
                        </td>
                        <td className="px-4 py-3">
                          <div>
                            <p className="font-medium">{t.from_user?.name || 'System'}</p>
                            <p className="text-xs text-muted-foreground">@{t.from_user?.username || 'system'}</p>
                          </div>
                        </td>
                        <td className="px-4 py-3">
                          <div>
                            <p className="font-medium">{t.to_user?.name || 'System'}</p>
                            <p className="text-xs text-muted-foreground">@{t.to_user?.username || 'system'}</p>
                          </div>
                        </td>
                        <td className="px-4 py-3 text-right">
                          <span className="font-bold text-emerald-500">₹{(t.amount || 0).toLocaleString()}</span>
                        </td>
                        <td className="px-4 py-3">
                          <p className="text-sm text-muted-foreground truncate max-w-[200px]" title={t.description}>
                            {t.description}
                          </p>
                        </td>
                        <td className="px-4 py-3 text-center">
                          <span className={`px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(t.status)}`}>
                            {t.status}
                          </span>
                        </td>
                        <td className="px-4 py-3 text-right text-sm text-muted-foreground">
                          {t.created_at ? new Date(t.created_at).toLocaleDateString() : 'N/A'}
                          <br />
                          <span className="text-xs">
                            {t.created_at ? new Date(t.created_at).toLocaleTimeString() : ''}
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              
              {/* Pagination — Wave 34 · smart page controls */}
              <div className="p-4 border-t border-white/10 flex items-center justify-between flex-wrap gap-2">
                <p className="text-sm text-muted-foreground">
                  Page {Math.floor(pagination.offset / pagination.limit) + 1} of {Math.max(1, Math.ceil(pagination.total / pagination.limit))} · Showing {pagination.offset + 1}-{Math.min(pagination.offset + pagination.limit, pagination.total)} of {pagination.total}
                </p>
                <div className="flex items-center gap-2">
                  <select value={pagination.limit}
                          onChange={(e) => setPagination(prev => ({ ...prev, limit: parseInt(e.target.value, 10), offset: 0 }))}
                          data-testid="ledger-page-size"
                          className="px-2 py-1.5 rounded-lg bg-white/10 text-xs">
                    {[50, 100, 200, 500].map(n => <option key={n} value={n}>{n} / page</option>)}
                  </select>
                  <button
                    onClick={() => setPagination(prev => ({ ...prev, offset: 0 }))}
                    disabled={pagination.offset === 0}
                    title="First"
                    data-testid="ledger-first"
                    className="px-2 py-1.5 rounded-lg bg-white/10 hover:bg-white/20 disabled:opacity-50 disabled:cursor-not-allowed text-xs font-black"
                  >⏮</button>
                  <button
                    onClick={() => setPagination(prev => ({ ...prev, offset: Math.max(0, prev.offset - prev.limit) }))}
                    disabled={pagination.offset === 0}
                    className="p-2 rounded-lg bg-white/10 hover:bg-white/20 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    <ChevronLeft className="w-4 h-4" />
                  </button>
                  <button
                    onClick={() => setPagination(prev => ({ ...prev, offset: prev.offset + prev.limit }))}
                    disabled={pagination.offset + pagination.limit >= pagination.total}
                    className="p-2 rounded-lg bg-white/10 hover:bg-white/20 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    <ChevronRight className="w-4 h-4" />
                  </button>
                  <button
                    onClick={() => setPagination(prev => ({ ...prev, offset: Math.max(0, (Math.ceil(prev.total / prev.limit) - 1) * prev.limit) }))}
                    disabled={pagination.offset + pagination.limit >= pagination.total}
                    title="Last"
                    data-testid="ledger-last"
                    className="px-2 py-1.5 rounded-lg bg-white/10 hover:bg-white/20 disabled:opacity-50 disabled:cursor-not-allowed text-xs font-black"
                  >⏭</button>
                </div>
              </div>
            </>
          ) : (
            <div className="p-12 text-center">
              <Wallet className="w-16 h-16 mx-auto mb-4 text-muted-foreground opacity-50" />
              <p className="text-muted-foreground">No transactions found</p>
              <p className="text-sm text-muted-foreground mt-2">
                Transactions will appear here as users make transfers, purchases, and earn commissions
              </p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default TransactionLedger;
